// @ts-check
// Equipe na SPA: membros, convites e papéis (docs/arquitetura-appcrm/08-contrato-equipe-permissoes.md, seção 4).
import { api } from './api-client.js';

/**
 * @typedef {{ id: string, userId: string | null, name: { firstName?: string, lastName?: string } | null, userEmail: string | null,
 *   avatarUrl: string | null, roleId: string, isActive: boolean, isCurrentUser?: boolean, createdAt?: string }} TeamMember
 * @typedef {{ id: string, email: string, roleId: string | null, expiresAt: string, lastSentAt: string, status: 'PENDING' | 'EXPIRED' | 'REVOKED' | 'ACCEPTED',
 *   inviteUrl?: string, emailSent?: boolean }} Invitation
 * @typedef {{ objectMetadataId: string, canReadObjectRecords: boolean | null, canUpdateObjectRecords: boolean | null,
 *   canSoftDeleteObjectRecords: boolean | null, canDestroyObjectRecords: boolean | null }} ObjectPermission
 * @typedef {{ objectMetadataId: string, fieldMetadataId: string, canReadFieldValue: false | null, canUpdateFieldValue: false | null }} FieldPermission
 * @typedef {{ id: string, standardKey: string | null, label: string, description: string | null, icon: string | null, isEditable: boolean,
 *   canUpdateAllSettings: boolean, canAccessAllTools: boolean, canReadAllObjectRecords: boolean, canUpdateAllObjectRecords: boolean,
 *   canSoftDeleteAllObjectRecords: boolean, canDestroyAllObjectRecords: boolean, canBeAssignedToUsers: boolean, position: number,
 *   permissionFlags: string[], objectPermissions: ObjectPermission[], fieldPermissions: FieldPermission[], workspaceMemberCount: number,
 *   isDefault: boolean, funnelStageScope?: FunnelStageScope | null }} Role
 * @typedef {{ visibleStageValues: string[], handoffStageValues: string[], visibleStageOptionIds?: string[], handoffStageOptionIds?: string[] }} FunnelStageScope
 *   etapas do funil que o papel vê (Ver) e para onde passa o card sem continuar vendo (Pode passar); nulo é "todas"
 *   (contrato de equipe, seção 3.2)
 */

/** @returns {Promise<TeamMember[]>} */
export async function listTeamMembers() {
  const body = await api.get('/rest/team/members');
  return body?.data?.members ?? [];
}

/** @param {string} workspaceMemberId @param {{ roleId?: string, isActive?: boolean }} patch */
export function updateTeamMember(workspaceMemberId, patch) {
  return api.patch(`/rest/team/members/${workspaceMemberId}`, patch);
}

/** @param {string} workspaceMemberId */
export function removeTeamMember(workspaceMemberId) {
  return api.delete(`/rest/team/members/${workspaceMemberId}`);
}

/** @returns {Promise<Invitation[]>} */
export async function listInvitations() {
  const body = await api.get('/rest/team/invitations');
  return body?.data?.invitations ?? [];
}

/**
 * @param {{ emails: string[], roleId?: string | null }} input
 * @returns {Promise<{ success: boolean, errors: string[], invitations: Invitation[] }>}
 */
export async function sendInvitations(input) {
  const body = await api.post('/rest/team/invitations', input);
  return body?.data ?? { success: false, errors: [], invitations: [] };
}

/** @param {string} invitationId @returns {Promise<Invitation>} */
export async function resendInvitation(invitationId) {
  const body = await api.post(`/rest/team/invitations/${invitationId}/resend`);
  return body?.data?.invitation;
}

/** @param {string} invitationId */
export function revokeInvitation(invitationId) {
  return api.delete(`/rest/team/invitations/${invitationId}`);
}

/**
 * invitableRoleIds: papéis com que quem lê pode convidar (o servidor recusa os demais).
 * @returns {Promise<{ roles: Role[], defaultRoleId: string, permissionFlags: string[], invitableRoleIds?: string[] }>}
 */
export async function listRoles() {
  const body = await api.get('/rest/team/roles');
  return body?.data ?? { roles: [], defaultRoleId: '', permissionFlags: [] };
}

/** @param {string | null} roleId @param {Partial<Role>} input @returns {Promise<Role>} */
export async function saveRole(roleId, input) {
  const body = roleId ? await api.put(`/rest/team/roles/${roleId}`, input) : await api.post('/rest/team/roles', input);
  return body?.data?.role;
}

/**
 * Membros que veem o card na etapa de destino do repasse (novo responsável, contrato de equipe, seção 3.2).
 * @param {string} enrollmentOpportunityId @param {string} stage
 * @returns {Promise<Array<{ id: string, name: { firstName?: string, lastName?: string } | null }>>}
 */
export async function listHandoffMembers(enrollmentOpportunityId, stage) {
  const query = new URLSearchParams({ enrollmentOpportunityId, stage });
  const body = await api.get(`/rest/team/handoff-members?${query}`);
  return Array.isArray(body?.data?.members) ? body.data.members : [];
}

/** @param {string} roleId */
export function deleteRole(roleId) {
  return api.delete(`/rest/team/roles/${roleId}`);
}

/** @param {string} roleId */
export function setDefaultRole(roleId) {
  return api.put('/rest/team/default-role', { roleId });
}

/**
 * Dados públicos do convite (tela de aceite).
 * @param {string} token
 * @returns {Promise<{ kind?: 'PERSONAL' | 'PUBLIC_LINK', email: string | null, status: Invitation['status'], expiresAt: string | null, tenant: { slug: string, displayName: string, logoUrl: string | null }, inviterName: string | null }>}
 */
export function fetchInvitation(token) {
  return api.get(`/auth/invitations/${encodeURIComponent(token)}`, undefined, { skipUnauthorizedRedirect: true });
}
